import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, ShoppingBag, Package, AlertTriangle, Users, Inbox, TrendingUp, MailCheck, MessageCircle, Coins, Send } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_COLORS = {
    pending: "text-neutral-700 border-neutral-300 bg-neutral-100",
    confirmed: "text-neutral-900 border-neutral-300 bg-neutral-100",
    processing: "text-amber-700 border-amber-400/30 bg-amber-400/10",
    shipped: "text-violet-300 border-violet-400/30 bg-violet-400/10",
    delivered: "text-emerald-700 border-emerald-400/30 bg-emerald-400/10",
    cancelled: "text-red-700 border-red-400/30 bg-red-400/10",
};

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [chatInsights, setChatInsights] = useState(null);
    const [chatCost, setChatCost] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/admin/stats")
            .then((r) => setStats(r.data))
            .catch((e) => setError(e.response?.data?.detail || e.message));
        api.get("/admin/chat/insights")
            .then((r) => setChatInsights(r.data))
            .catch(() => {});
        api.get("/admin/chat/cost")
            .then((r) => setChatCost(r.data))
            .catch(() => {});
    }, []);

    if (error) return <div className="text-red-400 text-sm" data-testid="admin-error">{error}</div>;
    if (!stats) return <div className="text-neutral-500 text-sm">Loading metrics…</div>;

    const tiles = [
        { label: "Revenue", value: `£${stats.revenue_total.toFixed(2)}`, icon: DollarSign, testid: "kpi-revenue" },
        { label: "Orders total", value: stats.orders_total, icon: ShoppingBag, testid: "kpi-orders-total" },
        { label: "Orders today", value: stats.orders_today, icon: TrendingUp, testid: "kpi-orders-today" },
        { label: "Products", value: stats.products_total, icon: Package, testid: "kpi-products" },
        { label: "Out of stock", value: stats.out_of_stock, icon: AlertTriangle, testid: "kpi-oos", warn: stats.out_of_stock > 0 },
        { label: "Users", value: stats.users_total, icon: Users, testid: "kpi-users" },
        { label: "Contact msgs", value: stats.contacts_unread, icon: Inbox, testid: "kpi-contacts" },
        {
            label: "Carts recovered",
            value: `${stats.cart_recovery.recovered} / ${stats.cart_recovery.reminders_sent}`,
            sub: stats.cart_recovery.recovered_revenue > 0 ? `£${stats.cart_recovery.recovered_revenue.toFixed(2)} recovered` : "via reminder emails",
            icon: MailCheck,
            testid: "kpi-cart-recovery",
        },
        {
            label: "Chat sessions (30d)",
            value: chatInsights ? chatInsights.total_sessions : "—",
            sub: chatInsights ? `${chatInsights.total_user_messages} questions · ${chatInsights.handoffs} handoffs` : "loading…",
            icon: MessageCircle,
            testid: "kpi-chat",
        },
        {
            label: "Chat spend (all-time)",
            value: chatCost ? `$${chatCost.total_cost_usd.toFixed(4)}` : "—",
            sub: chatCost ? `≈$${chatCost.monthly_projection_usd.toFixed(2)}/mo projection` : "loading…",
            icon: Coins,
            testid: "kpi-chat-cost",
            warn: chatCost && chatCost.monthly_projection_usd > 25,
        },
    ];

    return (
        <div data-testid="admin-dashboard">
            <div className="mb-8 sm:mb-10">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-2">// Overview</div>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter">
                    Operations dashboard
                </h1>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-px bg-white/5 mb-8 sm:mb-10 rounded-xl overflow-hidden">
                {tiles.map((t) => (
                    <div
                        key={t.label}
                        className="bg-white p-4 sm:p-6"
                        data-testid={t.testid}
                    >
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] text-neutral-500 mb-3">
                            <t.icon className={`w-3.5 h-3.5 ${t.warn ? "text-amber-400" : "text-[#0077b6]"}`} />
                            <span className="truncate">{t.label}</span>
                        </div>
                        <div className={`font-display text-2xl sm:text-3xl font-extrabold ${t.warn ? "text-amber-700" : "text-neutral-900"}`}>
                            {t.value}
                        </div>
                        {t.sub && (
                            <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mt-2 truncate">{t.sub}</div>
                        )}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <section className="border border-neutral-200 rounded-xl p-6" data-testid="status-breakdown">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-4">
                        // Order status breakdown
                    </div>
                    {Object.keys(stats.status_breakdown).length === 0 ? (
                        <div className="text-sm text-neutral-500">No orders yet.</div>
                    ) : (
                        <div className="space-y-3">
                            {Object.entries(stats.status_breakdown).map(([s, c]) => (
                                <div key={s} className="flex items-center justify-between">
                                    <span
                                        className={`text-[10px] uppercase tracking-[0.22em] border px-2 py-1 rounded ${
                                            STATUS_COLORS[s] || STATUS_COLORS.pending
                                        }`}
                                    >
                                        {s}
                                    </span>
                                    <span className="font-mono text-sm text-neutral-800">{c}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                <section className="border border-neutral-200 rounded-xl p-6" data-testid="top-products">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-4">
                        // Top sellers
                    </div>
                    {stats.top_products.length === 0 ? (
                        <div className="text-sm text-neutral-500">No sales data yet.</div>
                    ) : (
                        <div className="space-y-3">
                            {stats.top_products.map((p, i) => (
                                <div key={p.product_id} className="flex items-center gap-3 text-sm">
                                    <span className="font-mono text-[#0077b6] text-xs w-6">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="flex-1 truncate">{p.name}</span>
                                    <span className="font-mono text-xs text-neutral-500">{p.qty} units</span>
                                    <span className="font-mono text-xs text-neutral-700 w-20 text-right">
                                        £{p.revenue.toFixed(2)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {chatInsights && chatInsights.top_questions && chatInsights.top_questions.length > 0 && (
                <section
                    className="mt-6 border border-neutral-200 rounded-xl p-6"
                    data-testid="chat-insights-section"
                >
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6]">
                                // Chat insights — top questions (last {chatInsights.range_days}d)
                            </div>
                            <p className="text-xs text-neutral-500 mt-1">
                                What visitors are asking the AI assistant — spot content gaps and product-page copy improvements.
                            </p>
                        </div>
                        <div className="text-right text-xs text-neutral-500">
                            <div>{chatInsights.total_sessions} sessions</div>
                            <div>{chatInsights.avg_msgs_per_session} msgs / session</div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        {chatInsights.top_questions.map((q, i) => (
                            <div
                                key={`${q.question}-${i}`}
                                className="flex items-center gap-3 text-sm"
                                data-testid={`chat-question-${i}`}
                            >
                                <span className="font-mono text-[#0077b6] text-xs w-6 flex-shrink-0">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="flex-1 text-neutral-800 truncate">{q.question}</span>
                                <span className="font-mono text-xs text-neutral-500 flex-shrink-0">
                                    {q.count}×
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <YearlyWrapUpPanel />

            <div className="mt-10 flex flex-wrap gap-3">
                <Link
                    to="/admin/orders"
                    className="inline-flex items-center gap-2 bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs px-5 py-3"
                    data-testid="dashboard-go-orders"
                >
                    Manage orders
                </Link>
                <Link
                    to="/admin/inventory"
                    className="inline-flex items-center gap-2 border border-neutral-300 hover:border-[#0089c8] hover:text-black rounded-lg text-neutral-800 font-semibold uppercase tracking-[0.18em] text-xs px-5 py-3"
                    data-testid="dashboard-go-inventory"
                >
                    Inventory
                </Link>
                <Link
                    to="/admin/products"
                    className="inline-flex items-center gap-2 border border-neutral-300 hover:border-[#0089c8] hover:text-black rounded-lg text-neutral-800 font-semibold uppercase tracking-[0.18em] text-xs px-5 py-3"
                    data-testid="dashboard-go-products"
                >
                    Product editor
                </Link>
            </div>
        </div>
    );
}

function YearlyWrapUpPanel() {
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState(currentYear - 1);
    const [dry, setDry] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [sentSummary, setSentSummary] = useState(null);

    const preview = async () => {
        setLoading(true); setError(""); setSentSummary(null);
        try {
            const { data } = await api.post(`/admin/loyalty/wrap-up?year=${year}&dry_run=true`);
            setDry(data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setLoading(false);
        }
    };

    const sendAll = async () => {
        if (!dry || !window.confirm(`Send yearly wrap-up emails to ${dry.recipients} customer(s) for ${year}?`)) return;
        setSending(true); setError("");
        try {
            const { data } = await api.post(`/admin/loyalty/wrap-up?year=${year}&dry_run=false`);
            setSentSummary(data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            className="mt-6 border border-neutral-200 rounded-xl p-6"
            data-testid="yearly-wrapup-section"
        >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6]">
                        // Yearly wrap-up
                    </div>
                    <h2 className="font-display font-bold text-neutral-900 text-lg mt-1">
                        Your {year} with Nexus — recap emails
                    </h2>
                    <p className="text-xs text-neutral-500 mt-1">
                        Every customer who ordered in {year} gets a &ldquo;Your year with Nexus&rdquo; recap (orders, top compounds, points earned, year-end tier).
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <input
                        type="number"
                        min="2024"
                        max={currentYear}
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value, 10) || currentYear)}
                        className="w-24 bg-white border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-3 py-2 text-sm"
                        data-testid="wrapup-year"
                    />
                    <button
                        type="button"
                        onClick={preview}
                        disabled={loading}
                        className="inline-flex items-center gap-2 border border-neutral-300 hover:border-[#0089c8] hover:text-black rounded-lg text-neutral-800 font-semibold uppercase tracking-[0.18em] text-xs px-4 py-2.5"
                        data-testid="wrapup-preview"
                    >
                        {loading ? "Loading…" : "Preview"}
                    </button>
                    {dry && (
                        <button
                            type="button"
                            onClick={sendAll}
                            disabled={sending || dry.recipients === 0}
                            className="inline-flex items-center gap-2 bg-[#0089c8] hover:bg-[#0077b6] disabled:opacity-50 rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs px-4 py-2.5"
                            data-testid="wrapup-send"
                        >
                            <Send className="w-3 h-3" /> {sending ? "Sending…" : `Send ${dry.recipients}`}
                        </button>
                    )}
                </div>
            </div>
            {error && (
                <div className="text-xs text-red-600 mb-3" data-testid="wrapup-error">{error}</div>
            )}
            {dry && !sentSummary && (
                <div className="text-xs text-neutral-500 mb-3" data-testid="wrapup-preview-result">
                    {dry.recipients} customer(s) will receive a recap for {dry.year}. Sample preview below.
                </div>
            )}
            {sentSummary && (
                <div className="text-xs text-[#0077b6] font-semibold mb-3" data-testid="wrapup-sent">
                    Sent to {sentSummary.sent} customer(s) for {sentSummary.year}.
                </div>
            )}
            {dry && dry.preview && dry.preview.length > 0 && (
                <div className="border border-neutral-200 rounded-lg overflow-hidden">
                    <div className="grid grid-cols-[1.6fr_0.6fr_0.7fr_0.7fr_1fr] px-4 py-2 bg-neutral-50 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                        <div>Customer</div><div>Orders</div><div>Spent</div><div>Points</div><div>Tier @ year-end</div>
                    </div>
                    <div className="divide-y divide-neutral-200">
                        {dry.preview.map((row, i) => (
                            <div key={`${row.email}-${i}`} className="grid grid-cols-[1.6fr_0.6fr_0.7fr_0.7fr_1fr] px-4 py-2 text-xs items-center" data-testid={`wrapup-preview-row-${i}`}>
                                <div className="truncate">
                                    <div className="text-neutral-900 truncate">{row.name || "—"}</div>
                                    <div className="text-neutral-500 text-[10px] truncate">{row.email}</div>
                                </div>
                                <div className="font-mono">{row.stats.order_count}</div>
                                <div className="font-mono">£{row.stats.total_spent.toFixed(2)}</div>
                                <div className="font-mono text-[#0077b6] font-bold">{row.stats.points_earned}</div>
                                <div className="text-neutral-800">{row.stats.tier_label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}
